<template>
    <div class="club-overview">
        <div class="content">
            <div v-show="showWelcomeMessage" class="welcome-message">
                <Icon name="qr-code" size="36"></Icon>
                <div class="message-container">
                    <h2 class="copy-m bold">{{ $t('venue_overview.welcome_message.title') }}</h2>
                    <p class="copy-s">{{ $t('venue_overview.welcome_message.description') }}</p>
                </div>
                <Button
                    type="dark-secondary"
                    :value="$t('venue_overview.welcome_message.action')"
                    icon="download"
                    round
                    @click="openQRCode"
                />
                <FeatherIcon class="close" name="x" @click="showWelcomeMessage = false" />
            </div>
            <div class="sub-header">
                <div class="title-container">
                    <h1>
                        {{
                            $route.name === 'festivals'
                                ? 'Festivals'
                                : $route.name === 'promoters'
                                ? 'Promoters'
                                : $t('venue_overview.headline')
                        }}
                    </h1>
                    <DInput
                        v-model="search"
                        :search="true"
                        :placeholder="'Search'"
                        style="margin-bottom: 0"
                        aria-autocomplete="none"
                    ></DInput>
                    <Select
                        v-model="venueSort"
                        :placeholder="$t('venue_overview.sortDropdown.title')"
                        :options="[
                            {
                                value: 'name',
                                text: $t('venue_overview.sortDropdown.name'),
                            },
                            {
                                value: 'capacity',
                                text: $t('venue_overview.sortDropdown.capacity'),
                            },
                        ]"
                    >
                    </Select>
                    <Button
                        v-if="false"
                        type="primary"
                        :value="$t('venue_overview.create_venue')"
                        icon="plus"
                        round
                        @click="openCreateVenue"
                    />
                    <Button
                        v-if="signedInUser.isRootAdmin"
                        type="primary"
                        :value="
                            $route.name === 'festivals'
                                ? 'Import Festival'
                                : $route.name === 'promoters'
                                ? 'Import Promoter'
                                : $t('venue_overview.import_venue')
                        "
                        icon="plus"
                        round
                        @click="openImportVenue"
                    />
                </div>
                <div v-if="false" class="search-container">
                    <div class="search">
                        <FeatherIcon name="search" />
                        <input type="text" placeholder="34 Events durchsuchen..." />
                    </div>
                    <div class="filter-container">
                        <div class="filter">
                            <div class="text copy-m">Anstehende Veranstaltungen</div>
                            <FeatherIcon name="chevron-down" />
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="signedInUser && !venues.length" class="empty">
                <EmptyState
                    :message="{
                        title: $t('venue_overview.empty_state.title'),
                        description: $t('venue_overview.empty_state.description'),
                    }"
                    type="venues"
                >
                    <Button
                        v-if="false"
                        :value="$t('venue_overview.empty_state.button_connect_facebook')"
                        type="secondary"
                        round
                    />
                    <Button
                        :value="$t('venue_overview.empty_state.button_create_event')"
                        type="primary"
                        icon="plus"
                        round
                        @click="openCreateVenue"
                    />
                </EmptyState>
            </div>
            <div v-show="!signedInUser" class="list club-loader">
                <Club v-for="venue in new Array(3)" :key="venue" :loading="true" />
            </div>
            <div v-if="signedInUser" class="list club-list">
                <Club
                    v-for="venue in venueSort === 'capacity'
                        ? venues.slice().sort((a, b) => b.capacityUsage - a.capacityUsage)
                        : venues.slice().sort((a, b) => a.name.localeCompare(b.name))"
                    :key="venue.id"
                    :venue="venue"
                    :loading="$apollo.loading"
                    @click="$router.push({ name: 'venue', params: { id: venue.id } })"
                />
            </div>
            <div style="display: flex; flex: 2; margin-top: 2rem">
                <Button
                    v-if="currentPage > 1"
                    type="secondary"
                    value="Previous"
                    style="flex: 1; margin-right: 1rem"
                    @click="currentPage--"
                />
                <Button
                    v-if="currentPage < Math.floor(venues.length / 100)"
                    type="secondary"
                    value="Next"
                    style="flex: 1; margin-left: 1rem"
                    @click="currentPage++"
                />
            </div>
        </div>
        <Modal v-if="modalOpen" class="delete-event-modal" @cancel="modalOpen = false" @confirm="createVenue">
            <div class="modal-content">
                <div class="headline-container">
                    <div class="copy-l bold">{{ $t('venue_overview.modal.headline') }}</div>
                </div>
                <div class="main">
                    <DInput v-model="venueName" :placeholder="$t('venue_overview.modal.venue_name')"></DInput>
                </div>
                <div class="action-container fullwidth">
                    <Button type="secondary" :value="$t('venue_overview.modal.cancel')" @click="modalOpen = false" />
                    <Button type="primary" :value="$t('venue_overview.modal.create')" @click="createVenue" />
                </div>
            </div>
        </Modal>
        <Modal
            v-if="importVenueModalOpen"
            class="import-venue-modal"
            @cancel="importVenueModalOpen = false"
            @confirm="importVenueMutation"
        >
            <div class="modal-content">
                <div class="headline-container">
                    <div class="copy-l bold">
                        {{
                            $route.name === 'festivals'
                                ? 'Import Festival'
                                : $route.name === 'promoters'
                                ? 'Import Promoter'
                                : $t('venue_overview.importVenueModal.headline')
                        }}
                    </div>
                </div>
                <div class="main">
                    <DInput
                        v-model="facebookPageID"
                        :placeholder="$t('venue_overview.importVenueModal.venue_name')"
                        aria-autocomplete="none"
                    ></DInput>
                    <Select
                        v-if="$route.name === 'venues'"
                        v-model="venueCategory"
                        :placeholder="$t('venue_overview.importVenueModal.venueCategory')"
                        :options="[
                            {
                                value: 'Club',
                                text: 'Club',
                            },
                            {
                                value: 'Bar',
                                text: 'Bar',
                            },
                        ]"
                    >
                    </Select>
                </div>
                <div class="action-container fullwidth">
                    <Button
                        type="secondary"
                        :value="$t('venue_overview.importVenueModal.cancel')"
                        @click="importVenueModalOpen = false"
                    />
                    <Button
                        type="primary"
                        :value="
                            $route.name === 'festivals'
                                ? 'Import Festival'
                                : $route.name === 'promoters'
                                ? 'Import Promoter'
                                : $t('venue_overview.importVenueModal.create')
                        "
                        @click="importVenueMutation"
                    />
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import Club from '@/components/Club'
import gql from 'graphql-tag'
import EmptyState from '@/components/EmptyState'
import Button from '@/components/Button'
import Modal from '@/components/Modal'
import DInput from '@/components/Input'
import FeatherIcon from '@/components/FeatherIcon'
import Icon from '@/components/Icon'
import Select from '@/components/Select'

export default {
    name: 'VenueOverview',
    components: {
        Club,
        EmptyState,
        Button,
        Modal,
        DInput,
        FeatherIcon,
        Icon,
        Select,
    },
    data() {
        return {
            clubs: new Array(10),
            modalOpen: false,
            venueName: '',
            showWelcomeMessage: true,
            importVenueModalOpen: false,
            facebookPageID: '',
            venueCategory: '',
            search: '',
            venueSort: '',
            isRootAdmin: undefined,
            signedInUser: '',
            countVenues: '',
            currentPage: 1,
        }
    },
    apollo: {
        venues: {
            query: gql`
                query ($filter: VenuesFilter, $search: String, $offset: Int) {
                    venues(filter: $filter, search: $search, limit: 100, offset: $offset, sortDirection: Ascending) {
                        id
                        name
                        priceLevel
                        sourceFanCount
                        sourceStarRating
                        priceLevel
                        logoUrl
                        capacity
                        capacityInUse
                        capacityUsage
                        genres
                    }
                }
            `,
            variables() {
                return {
                    filter: {
                        category:
                            this.$route.name === 'festivals'
                                ? 'Festival'
                                : this.$route.name === 'promoters'
                                ? 'Promoter'
                                : undefined,
                        managedBySignedInBusinessUser: this.signedInUser.isRootAdmin ? false : true,
                    },
                    search: this.search,
                    offset: this.search ? 0 : (this.currentPage - 1) * 100,
                }
            },
            pollInterval: 5000,
        },
        signedInUser: {
            query: gql`
                query {
                    signedInUser {
                        isRootAdmin
                        venueAccess {
                            venue {
                                id
                                name
                            }
                        }
                    }
                }
            `,
        },
        // countVenues: {
        //     query: gql`
        //         query ($filter: VenuesFilter) {
        //             countVenues(filter: $filter)
        //         }
        //     `,
        //     variables() {
        //         return {
        //             filter: {
        //                 managedBySignedInBusinessUser: this.signedInUser.isRootAdmin ? false : true,
        //             },
        //         }
        //     },
        // },
    },
    methods: {
        async openQRCode() {
            this.isQRCodeLoading = true

            try {
                const res = await this.$apollo.mutate({
                    mutation: gql`
                        mutation ($id: ID!) {
                            generateVenueCheckinPDF(venueID: $id, webCheckinFallback: true)
                        }
                    `,
                    variables: {
                        id: this.signedInUser.venueAccess[0].venue.id,
                    },
                })

                this.downloadURI(
                    res.data.generateVenueCheckinPDF,
                    `${this.signedInUser.venueAccess[0].venue.name} Check-in QR Code.pdf`,
                )
            } catch (e) {
                console.log(this.signedInUser)
                this.$pm.createToast('error', this.$t('errors.qr_code_could_not_be_opened'))
            }

            this.isQRCodeLoading = false
        },
        downloadURI(uri, name) {
            let link = document.createElement('a')
            link.download = name
            link.href = uri
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        openCreateVenue() {
            this.modalOpen = true
        },
        openImportVenue() {
            this.importVenueModalOpen = true
        },
        async createVenue() {
            let venueInput = {
                category: 'Club',
                city: '',
                country: '',
                genres: [],
                imageUrls: [],
                location: {
                    latitude: 0.0,
                    longitude: 0.0,
                },
                logoUrl: 'https://picsum.photos/seed/partymate/500/500',
                name: this.venueName,
                paymentMethods: [],
                source: 'PartyMate',
            }

            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($venueInput: VenueInput!) {
                            createVenue(input: $venueInput) {
                                id
                            }
                        }
                    `,
                    variables: {
                        venueInput: venueInput,
                    },
                })
                .then((data) => {
                    this.modalOpen = false
                    this.venueName = ''
                    this.$router.push({ name: 'club-settings', params: { id: data.data.createVenue.id } })
                })
                .catch(() => {
                    this.$pm.createToast('error', this.$t('errors.venue_could_not_be_created'))
                })
        },
        async importVenueMutation() {
            if (this.venueCategory === '') {
                await this.$apollo
                    .mutate({
                        mutation: gql`
                            mutation ($pageID: ID!, $category: PageCategory, $override: Boolean) {
                                syncVenueWithFacebook(pageID: $pageID, category: $category, override: $override) {
                                    id
                                }
                            }
                        `,
                        variables: {
                            pageID: this.facebookPageID,
                            category:
                                this.$route.name === 'festivals'
                                    ? 'Festival'
                                    : this.$route.name === 'promoters'
                                    ? 'Promoter'
                                    : undefined,
                            override:
                                this.$route.name === 'festivals'
                                    ? true
                                    : this.$route.name === 'promoters'
                                    ? true
                                    : undefined,
                        },
                    })
                    .then((data) => {
                        this.modalOpen = false
                        this.venueName = ''
                        this.$router.push({ name: 'club-settings', params: { id: data.data.syncVenueWithFacebook.id } })
                    })
                    .catch((e) => {
                        console.log(e)
                        this.$pm.createToast('error', this.$t('errors.venue_could_not_be_created'))
                    })
            } else {
                await this.$apollo
                    .mutate({
                        mutation: gql`
                            mutation ($pageID: ID!, $category: PageCategory, $override: Boolean) {
                                syncVenueWithFacebook(pageID: $pageID, category: $category, override: $override) {
                                    id
                                }
                            }
                        `,
                        variables: {
                            pageID: this.facebookPageID,
                            category:
                                this.$route.name === 'festivals'
                                    ? 'Festival'
                                    : this.$route.name === 'promoters'
                                    ? 'Festival'
                                    : this.venueCategory,
                            override:
                                this.$route.name === 'festivals'
                                    ? true
                                    : this.$route.name === 'promoters'
                                    ? true
                                    : undefined,
                        },
                    })
                    .then((data) => {
                        this.modalOpen = false
                        this.venueName = ''
                        this.$router.push({ name: 'club-settings', params: { id: data.data.syncVenueWithFacebook.id } })
                    })
                    .catch((e) => {
                        console.log(e)
                        this.$pm.createToast('error', this.$t('errors.venue_could_not_be_created'))
                    })
            }
        },
    },
}
</script>

<style scoped lang="sass">
.club-overview
    .welcome-message
        padding: $m
        border-radius: 12px
        margin-bottom: $xl
        background-color: $neutral-black
        display: grid
        grid-template-columns: 32px auto auto 24px
        grid-gap: 24px
        align-items: center
        @include breakpoint('m')
            grid-template-columns: 32px auto
            grid-template-areas: "code msg" "button button"

            .qr-code
                grid-area: code

            .message-container
                grid-area: msg

            .button
                grid-area: button

            .close
                display: none

        .icon::v-deep svg
            path
                stroke: $neutral-white

        .message-container
            h2, p
                color: $neutral-white

        .close
            cursor: pointer

    .sub-header
        margin-bottom: $xl

        .title-container
            margin-bottom: $l
            @include breakpoint('m')
                display: flex
                flex-direction: column
                align-items: flex-start

                .input
                    margin-top: 12px
                    width: 100%

.input
    margin-bottom: $xs

.select-options
    max-height: 100px
    overflow-y: scroll
    border-radius: 5px
    margin-bottom: $s
    box-shadow: 0px 0px 0px rgba(6, 44, 82, 0.1), 0px 1px 3px rgba(64, 66, 69, 0.12), 0px 2px 16px rgba(33, 43, 54, 0.08)
    padding: $s $xxs

    .select-option
        display: flex
        min-width: 0
        align-items: center
        height: 3.2rem
        padding: 0 $s
        cursor: pointer
        border:
            radius: 5px
        &:hover
            background: $gray-1
</style>
